// Attendance.tsx
import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Calendar,
  Download,
  Eye,
  User,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Users,
  Activity,
  Filter,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  Briefcase,
  Menu
} from "lucide-react";
import {
  getAttendanceDayWise,
  getAttendanceSummary,
  getAttendanceStatus,
  exportAttendanceToCSV,
  AttendanceWorkingHour,
  AttendanceStatus,
  TodayAttendance,
  PieData
} from "../api/attendance";

const Attendance = () => {
  const [pieData, setPieData] = useState<PieData[]>([]);
  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance[]>([]);
  const [employeeStatus, setEmployeeStatus] = useState<AttendanceStatus[]>([]);
  const [filteredEmployeeStatus, setFilteredEmployeeStatus] = useState<AttendanceStatus[]>([]);
  const [startDate, setStartDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "Present" | "Absent">("all");
  const [showInfoPopup, setShowInfoPopup] = useState(false);

  // Accordion states
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [isStatusTableOpen, setIsStatusTableOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<TodayAttendance | null>(null);

  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedEmployeeStatus, setSelectedEmployeeStatus] = useState<AttendanceStatus | null>(null);
  
  // State for attendance working hours data
  const [attendanceWorkingHours, setAttendanceWorkingHours] = useState<AttendanceWorkingHour[]>([]);
  const [isLoadingWorkingHours, setIsLoadingWorkingHours] = useState(false);

  const fetchData = async (start: string, end: string) => {
    setLoading(true);
    try {
      // Fetch attendance summary
      const summaryData = await getAttendanceSummary(start, end);
      setPieData(summaryData);

      // Fetch attendance status for the start date
      const statusData = await getAttendanceStatus(start);
      setEmployeeStatus(statusData);
      setFilteredEmployeeStatus(statusData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchData(startDate, endDate);
    setTimeout(() => setIsRefreshing(false), 500);
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  }, [startDate, endDate]);

  // Filter employees based on search and status
  useEffect(() => {
    let filtered = [...employeeStatus];

    if (searchQuery) {
      filtered = filtered.filter(emp =>
        emp.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(emp => emp.status === statusFilter);
    }

    setFilteredEmployeeStatus(filtered);
  }, [searchQuery, statusFilter, employeeStatus]);

  // Handle employee click to fetch day-wise working hours
  const handleEmployeeClick = async (emp: AttendanceStatus) => {
    try {
      setIsLoadingWorkingHours(true);
      const workingHoursData = await getAttendanceDayWise(emp.user_id!, startDate, endDate);
      setAttendanceWorkingHours(workingHoursData);
      setSelectedEmployeeStatus(emp);
      setStatusDialogOpen(true);
    } catch (error: any) {
      console.error("Error fetching attendance working hours:", error);
    } finally {
      setIsLoadingWorkingHours(false);
    }
  };

  const getStatusIcon = (status: string) => {
    if (status === "Present") {
      return <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500" />;
    }
    return <XCircle className="w-3 h-3 sm:w-4 sm:h-4 text-red-500" />;
  };

  // Calculate statistics
  const totalEmployees = employeeStatus.length;
  const presentCount = employeeStatus.filter(emp => emp.status === "Present").length;
  const absentCount = totalEmployees - presentCount;
  const attendancePercentage = totalEmployees > 0 ? ((presentCount / totalEmployees) * 100).toFixed(1) : 0;

  const handleStartDateChange = (date: string) => {
    setStartDate(date);
    if (date > endDate) {
      setEndDate(date);
    }
    setShowInfoPopup(true);
  };

  const handleEndDateChange = (date: string) => {
    if (date >= startDate) {
      setEndDate(date);
    }
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    const today = new Date().toISOString().split("T")[0];
    setStartDate(today);
    setEndDate(today);
  };

  const isFilterActive = searchQuery !== "" || statusFilter !== "all" ||
    startDate !== new Date().toISOString().split("T")[0] ||
    endDate !== new Date().toISOString().split("T")[0];

  // Calculate summary statistics for the employee
  const getEmployeeSummary = () => {
    if (attendanceWorkingHours.length === 0) return null;
    
    const totalWorkingHours = attendanceWorkingHours.reduce((sum, record) => sum + record.total_hours, 0);
    const averageWorkingHours = totalWorkingHours / attendanceWorkingHours.length;
    const totalDays = attendanceWorkingHours.length;
    const presentDays = attendanceWorkingHours.filter(record => record.In_time && record.In_time !== "00:00:00").length;
    
    return {
      totalDays,
      presentDays,
      absentDays: totalDays - presentDays,
      totalWorkingHours: totalWorkingHours.toFixed(2),
      averageWorkingHours: averageWorkingHours.toFixed(2)
    };
  };

  const employeeSummary = getEmployeeSummary();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 sm:h-32 sm:w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="gt-shell min-h-screen">
      <div className="max-w-7xl mx-auto p-4 md:p-7 space-y-6 gt-fade-in">
        {/* Header Section */}
        <div className="gt-page-hero flex-col md:flex-row gap-4">
          <div>
            <div className="gt-eyebrow-row">
              <span className="gt-leaf" />
              <span className="gt-eyebrow">iii. Operations</span>
            </div>
            <h1 className="gt-page-title">Attendance <em>Management</em></h1>
            <p className="gt-page-sub" style={{ marginTop: 6 }}>
              Track and manage employee <em className="gt-serif-italic">attendance</em> records.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="gt-icon-btn sm:hidden"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <button
              onClick={handleRefresh}
              className="gt-icon-btn"
              disabled={isRefreshing}
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Stats Tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="gt-stat">
            <div className="gt-stat-label">
              <Users className="w-3.5 h-3.5" /> Total Employees
            </div>
            <p className="gt-stat-value">{totalEmployees}</p>
          </div>
          <div className="gt-stat">
            <div className="gt-stat-label">
              <CheckCircle className="w-3.5 h-3.5" /> Present Today
            </div>
            <p className="gt-stat-value" style={{ color: 'var(--gt-accent-strong)' }}>
              {presentCount}
            </p>
          </div>
          <div
            className="gt-stat"
            style={{
              borderColor:
                absentCount > 0 ? 'rgba(220,38,38,0.18)' : undefined,
            }}
          >
            <div className="gt-stat-label">
              <XCircle className="w-3.5 h-3.5" /> Absent Today
            </div>
            <p
              className="gt-stat-value"
              style={{ color: absentCount > 0 ? '#991b1b' : undefined }}
            >
              {absentCount}
            </p>
          </div>
          <div className="gt-stat">
            <div className="gt-stat-label">
              <Activity className="w-3.5 h-3.5" /> Attendance Rate
            </div>
            <p className="gt-stat-value">
              {attendancePercentage}
              <span style={{ fontSize: 18, marginLeft: 4, color: 'var(--gt-text-tertiary)' }}>
                %
              </span>
            </p>
          </div>
        </div>

      {/* Advanced Filter Section - Collapsible */}
      <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
        <div
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Filter className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  <span className="hidden sm:inline">Advanced Filters</span>
                  <span className="sm:hidden">Filters</span>
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">Filter attendance records</CardDescription>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {isFilterActive && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      clearAllFilters();
                    }}
                    className="text-xs sm:text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden xs:inline">Clear</span>
                  </button>
                )}
                {isFilterOpen ? (
                  <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
        </div>

        {isFilterOpen && (
          <CardContent className="pt-3 sm:pt-4 animate-in slide-in-from-top-2 duration-200">
            <div className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Start Date</label>
                  <div className="flex items-center gap-1 sm:gap-2 bg-white border border-gray-200 rounded-lg p-1.5 sm:p-2 hover:border-blue-300 focus-within:border-blue-500 transition-colors">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      className="flex-1 border-0 focus:ring-0 text-xs sm:text-sm bg-transparent focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">End Date</label>
                  <div className="flex items-center gap-1 sm:gap-2 bg-white border border-gray-200 rounded-lg p-1.5 sm:p-2 hover:border-blue-300 focus-within:border-blue-500 transition-colors">
                    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-blue-500" />
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => handleEndDateChange(e.target.value)}
                      className="flex-1 border-0 focus:ring-0 text-xs sm:text-sm bg-transparent focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Search Employee</label>
                  <div className="relative">
                    <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 sm:w-4 sm:h-4" />
                    <input
                      type="text"
                      placeholder="Search by employee name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-7 sm:pl-9 pr-2 sm:pr-3 py-1.5 sm:py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Status Filter</label>
                  <div className="flex gap-1.5 sm:gap-2">
                    <button
                      onClick={() => setStatusFilter("all")}
                      className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${statusFilter === "all"
                          ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setStatusFilter("Present")}
                      className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-0.5 sm:gap-1 ${statusFilter === "Present"
                          ? "bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Present</span>
                      <span className="xs:hidden">Pres</span>
                    </button>
                    <button
                      onClick={() => setStatusFilter("Absent")}
                      className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-0.5 sm:gap-1 ${statusFilter === "Absent"
                          ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                    >
                      <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Absent</span>
                      <span className="xs:hidden">Abs</span>
                    </button>
                  </div>
                </div>
              </div>

              {filteredEmployeeStatus.length !== employeeStatus.length && (
                <div className="p-2 sm:p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-xs sm:text-sm gap-2">
                    <span className="text-blue-700 font-medium">
                      <strong>{filteredEmployeeStatus.length}</strong> of <strong>{employeeStatus.length}</strong> employees match
                    </span>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs">
                      {searchQuery && (
                        <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs truncate max-w-[150px]">
                          🔍 {searchQuery}
                        </span>
                      )}
                      {statusFilter !== "all" && (
                        <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">
                          📊 {statusFilter}
                        </span>
                      )}
                      {(startDate !== new Date().toISOString().split("T")[0] || endDate !== new Date().toISOString().split("T")[0]) && (
                        <span className="text-blue-600 bg-blue-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs">
                          📅 {startDate.substring(5)} → {endDate.substring(5)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Employee Status Table - Collapsible with horizontal scroll for mobile */}
      <Card className="bg-white border-0 shadow-md hover:shadow-lg transition-all mb-4 sm:mb-6 overflow-hidden">
        <div
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsStatusTableOpen(!isStatusTableOpen)}
        >
          <CardHeader className="pb-2 sm:pb-3 border-b border-gray-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <CardTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <span className="hidden sm:inline">Employee Status</span>
                  <span className="sm:hidden">Status</span>
                </CardTitle>
                <CardDescription className="hidden sm:block text-gray-500">Present / Absent status for selected date range</CardDescription>
              </div>
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
                <div className="flex items-center gap-1 sm:gap-2 bg-gray-50 rounded-lg px-1.5 sm:px-3 py-0.5 sm:py-1.5">
                  <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                  <span className="text-[10px] sm:text-sm text-gray-600">{startDate.substring(5)} → {endDate.substring(5)}</span>
                </div>
                {isStatusTableOpen ? (
                  <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                )}
              </div>
            </div>
          </CardHeader>
        </div>

        {isStatusTableOpen && (
          <CardContent className="pt-3 sm:pt-4 animate-in slide-in-from-top-2 duration-200">
            <div className="overflow-x-auto -mx-3 sm:mx-0">
              <div className="min-w-[280px] sm:min-w-full">
                <table className="w-full text-xs sm:text-sm">
                  <thead>
                    <tr className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Employee</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">In Time</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left text-[10px] sm:text-xs font-semibold text-gray-600 uppercase tracking-wider">Out Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredEmployeeStatus?.map((emp, idx) => (
                      <tr key={idx} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-transparent transition-colors group cursor-pointer" onClick={() => handleEmployeeClick(emp)}>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <div className="flex items-center gap-1.5 sm:gap-3">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center shadow-sm flex-shrink-0">
                              <span className="text-[10px] sm:text-xs font-semibold text-gray-700">
                                {emp?.name?.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors text-xs sm:text-sm truncate max-w-[100px] sm:max-w-none">
                              {emp?.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4">
                          <Badge
                            className={`${emp.status === "Present"
                                ? "bg-gradient-to-r from-green-100 to-green-200 text-green-700"
                                : "bg-gradient-to-r from-red-100 to-red-200 text-red-700"
                              } font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded-full flex items-center gap-0.5 sm:gap-1 w-fit shadow-sm text-[10px] sm:text-xs`}
                          >
                            {getStatusIcon(emp.status)}
                            {emp.status === "Present" ? "Present" : "Absent"}
                          </Badge>
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-600 font-mono text-[10px] sm:text-sm">
                          {emp?.clock_in_time || "-"}
                        </td>
                        <td className="py-2 sm:py-3 px-2 sm:px-4 text-gray-600 font-mono text-[10px] sm:text-sm">
                          {emp?.out_time || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredEmployeeStatus.length === 0 && (
                <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">
                  No employees found matching your filters
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Attendance Records Section */}
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col xs:flex-row justify-between items-start xs:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-orange-500 to-red-600 rounded-full"></div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
              <span className="hidden sm:inline">Attendance Records</span>
              <span className="sm:hidden">Records</span>
            </h2>
            <p className="hidden sm:block text-gray-500 text-sm mt-1">Detailed attendance logs for today date</p>
          </div>
          <button
            onClick={() => exportAttendanceToCSV(employeeStatus)}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium hover:shadow-lg transition-all flex items-center gap-1 sm:gap-2"
          >
            <Download className="w-3 h-3 sm:w-4 sm:h-4" />
            <span className="hidden xs:inline">Export CSV</span>
            <span className="xs:hidden">Export</span>
          </button>
        </div>

        {todayAttendance.length === 0 ? (
          <div className="text-center py-8 sm:py-12 bg-white rounded-lg border border-gray-200 shadow-sm">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 text-xs sm:text-sm">No attendance records found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {todayAttendance.map((rec, idx) => (
              <Card
                key={idx}
                className="group bg-white border border-gray-200 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1 overflow-hidden"
                onClick={() => {
                  setSelectedRecord(rec);
                  setOpenDialog(true);
                }}
              >
                <div className="h-0.5 sm:h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 group-hover:from-purple-500 group-hover:to-pink-500 transition-all"></div>
                <CardHeader className="pb-2 sm:pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <Avatar className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 bg-gradient-to-br from-blue-500 to-purple-600 shadow-md">
                        <AvatarFallback className="bg-transparent text-white font-bold text-sm sm:text-base md:text-lg">
                          {rec.name ? rec.name[0].toUpperCase() : "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-sm sm:text-base font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {rec.name}
                        </CardTitle>
                        <CardDescription className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-0.5 sm:gap-1 mt-0.5">
                          <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                          {rec.date}
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 transition-all rounded-full p-1 sm:p-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRecord(rec);
                        setOpenDialog(true);
                      }}
                    >
                      <Eye className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-1.5 sm:p-2 text-center">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Check In</p>
                      <p className="font-semibold text-gray-700 text-xs sm:text-sm">
                        {rec.in_time || "N/A"}
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-1.5 sm:p-2 text-center">
                      <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">Check Out</p>
                      <p className="font-semibold text-gray-700 text-xs sm:text-sm">
                        {rec.end_time || "N/A"}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center pt-1.5 sm:pt-2 border-t border-gray-100">
                    <span className="text-[10px] sm:text-xs text-gray-500">Total Time</span>
                    <Badge
                      className={`${rec.in_time
                          ? "bg-gradient-to-r from-green-100 to-green-200 text-green-700"
                          : "bg-gradient-to-r from-red-100 to-red-200 text-red-700"
                        } font-medium px-2 sm:px-3 py-0.5 sm:py-1 rounded-full shadow-sm text-[10px] sm:text-xs`}
                    >
                      {rec.in_time ? "Present" : "Absent"}
                    </Badge>
                  </div>
                  {rec.total_time && (
                    <div className="mt-1.5 sm:mt-2 text-right">
                      <span className="text-[10px] sm:text-xs text-gray-500">
                        ⏱️ {rec.total_time}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Logs Dialog - Responsive */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
              Attendance Details
            </DialogTitle>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">{selectedRecord?.name} - {selectedRecord?.date}</p>
          </DialogHeader>
          <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
            {selectedRecord ? (
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                {selectedRecord.in_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Check In</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.in_time}</span>
                  </div>
                )}
                {selectedRecord.additional_out_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Additional Out</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.additional_out_time}</span>
                  </div>
                )}
                {selectedRecord.additional_in_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Additional In</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.additional_in_time}</span>
                  </div>
                )}
                {selectedRecord.end_time && (
                  <div className="flex justify-between items-center py-1.5 sm:py-2 border-b border-gray-200">
                    <span className="text-xs sm:text-sm text-gray-600">Final Out</span>
                    <span className="text-xs sm:text-sm font-medium text-gray-900">{selectedRecord.end_time}</span>
                  </div>
                )}
                {selectedRecord.total_time && (
                  <div className="flex justify-between items-center pt-1.5 sm:pt-2">
                    <span className="text-xs sm:text-sm font-semibold text-gray-700">Total Time</span>
                    <span className="text-base sm:text-lg font-bold text-blue-600">{selectedRecord.total_time}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8 text-gray-500 text-xs sm:text-sm">No logs available</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Attendance Working Hours Dialog - Responsive with horizontal scroll */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-3xl md:max-w-4xl max-h-[85vh] overflow-y-auto rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Attendance Details - {selectedEmployeeStatus?.name}
            </DialogTitle>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
              <p className="text-xs sm:text-sm text-gray-500">
                Date Range: {startDate} to {endDate}
              </p>
              {employeeSummary && (
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
                  <span className="bg-green-100 text-green-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">✅ Present: {employeeSummary.presentDays}</span>
                  <span className="bg-red-100 text-red-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">❌ Absent: {employeeSummary.absentDays}</span>
                  <span className="bg-blue-100 text-blue-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">⏱️ Total: {employeeSummary.totalWorkingHours}h</span>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {isLoadingWorkingHours ? (
            <div className="flex items-center justify-center py-8 sm:py-12">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-gray-600">Loading attendance data...</span>
            </div>
          ) : attendanceWorkingHours.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <Clock className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-300 mb-2 sm:mb-3" />
              <p className="text-xs sm:text-sm">No attendance records found for the selected date range</p>
            </div>
          ) : (
            <div className="mt-3 sm:mt-4 overflow-x-auto -mx-4 sm:mx-0">
              <div className="min-w-[500px] sm:min-w-full">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 sticky top-0">
                    <tr>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Date</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Shift</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">In Time</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Out Time</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Total Hours</th>
                      <th className="py-2 sm:py-3 px-2 sm:px-4 text-left font-semibold text-gray-700 text-[10px] sm:text-xs">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {attendanceWorkingHours.map((record, idx) => {
                      const isPresent = record.In_time && record.In_time !== "00:00:00";
                      return (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2 sm:py-3 px-2 sm:px-4 font-medium text-gray-900 text-[10px] sm:text-xs whitespace-nowrap">
                            {new Date(record.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex items-center gap-0.5 sm:gap-1">
                              <Briefcase className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-gray-400" />
                              <span className="text-gray-600 text-[10px] sm:text-xs">{record.shift_duration}</span>
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <span className={`font-mono text-[10px] sm:text-xs ${isPresent ? 'text-green-600' : 'text-gray-400'}`}>
                              {record.In_time && record.In_time !== "00:00:00" ? record.In_time : "-"}
                            </span>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <span className="font-mono text-[10px] sm:text-xs text-gray-600">
                              {record.out_time && record.out_time !== "00:00:00" ? record.out_time : "-"}
                            </span>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <div className="flex flex-col">
                              <span className="font-semibold text-blue-600 text-[10px] sm:text-xs">{record.total_working_hours}</span>
                              <span className="text-[8px] sm:text-[10px] text-gray-400 hidden sm:inline">{record.total_working_hours_words}</span>
                            </div>
                           </td>
                          <td className="py-2 sm:py-3 px-2 sm:px-4">
                            <Badge
                              className={`${isPresent
                                  ? "bg-gradient-to-r from-green-100 to-green-200 text-green-700"
                                  : "bg-gradient-to-r from-red-100 to-red-200 text-red-700"
                                } font-semibold px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full w-fit shadow-sm text-[9px] sm:text-xs`}
                            >
                              {isPresent ? "Present" : "Absent"}
                            </Badge>
                           </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Info Popup Dialog - Responsive */}
      <Dialog open={showInfoPopup} onOpenChange={setShowInfoPopup}>
        <DialogContent className="max-w-[90%] sm:max-w-md rounded-lg text-center">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">Information</DialogTitle>
          </DialogHeader>
          <p className="text-xs sm:text-sm text-gray-600 mt-2">
            Below table contains data for selected start date.
            <br />
            To see full data click on employee name.
          </p>
          <div className="mt-3 sm:mt-4">
            <Button onClick={() => setShowInfoPopup(false)} className="text-xs sm:text-sm">OK</Button>
          </div>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default Attendance;